'use client';

import { ReceiptEmailDialog } from '@/features/payment/ui/ReceiptEmailDialog';
import { RequiredSignInMethodOverlay } from '@/features/settings/ui/SignInPrompts';
import React, { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuthStore } from '@/app/model/stores';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import { MobileNavigation } from '@/app/ui/MobileNavigation';
import { SubscriptionNotifications } from '@/features/subscription';
import { GlobalLoadingBar } from '@/shared/ui';
import { useIsLoading } from '@/shared/loading';
import { useLayoutConfig } from '@/features/design/concept/hooks/use-layout-config';
import { KeyboardShortcutsHelp } from '@/shared/ui';
import { useJobQueue } from '@/features/design/job-queue';
import { FirstRunGate } from '@/features/first-run';
import { FeedbackQuickDialog } from '@/features/support';
import { AppRatingAutoStart } from '@/features/app-rating';
import { LoadingSpinner } from '@/shared/ui';
import { NewProjectDialog, ActiveProjectSync } from '@/features/project';
import { cn } from '@/shared/lib/cn';
import { AssistantRoot, useAssistantStore } from '@/features/assistant';
import { withRedirectParam } from '@/features/auth/lib/safe-redirect';

// Force dynamic rendering for all authenticated pages
export const dynamic = 'force-dynamic';

export default function ProtectedLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const router = useRouter();

  // Check if current page is history page
  const isHistoryPage =
    pathname === '/history' || pathname.startsWith('/history/');

  // Check if current page is the help center (top-level, reachable from the
  // main sidebar — no longer an account sub-page).
  const isHelpPage = pathname === '/help' || pathname.startsWith('/help/');

  // Check if current page is account page (hub or subpage)
  const isAccountPage =
    pathname === '/account' || pathname.startsWith('/account/');

  // Pages that use full-height desktop layout with sidebar (h-dvh at lg+)
  const hasFullHeightDesktopLayout =
    pathname.startsWith('/project/') ||
    isHistoryPage ||
    isHelpPage ||
    isAccountPage;

  // Auth state from store
  const loading = useAuthStore(
    (state) => state.isLoggingIn || state.isRegistering || state.isLoggingOut
  );
  // Two different questions, deliberately answered by two different flags.
  // `isHydrated` = "the stored session has been read" (synchronous, no
  // request) and it is what this layout PAINTS on. `isInitialized` = "the
  // server ruled on that token" (a round trip) and it is reserved for the one
  // decision where being wrong is harmful: routing an unverified account.
  const isHydrated = useAuthStore((state) => state.isHydrated);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const pendingEmailVerification = useAuthStore(
    (state) => state.pendingEmailVerification
  );

  const isGlobalLoading = useIsLoading();

  // Use the new hook that combines pathname-based config with store data
  const layoutConfig = useLayoutConfig();
  const isAssistantOpen = useAssistantStore((s) => s.isOpen);

  // Initialize job queue - this will only fetch if the queue is empty
  // WebSocket provider handles initial sync on connection, but we need this
  // for cases where the component mounts after WebSocket is already connected
  useJobQueue(10);

  // Auth gating is fully client-side (mobile parity); these effects redirect
  // unauthenticated/unverified users via the App Router (no full reload).
  const needsEmailVerification =
    isInitialized && isAuthenticated && pendingEmailVerification;
  // While the browser is being tried as a sign-in method (ADR-001) the
  // sign-in screen must not flash: hold the spinner instead.
  const deviceSignInPending = useAuthStore((state) => state.deviceSignInPending);
  const needsLogin = isHydrated && !isAuthenticated && !deviceSignInPending;

  // The one case that still waits for the server: an account whose stored
  // snapshot says the address was never confirmed. Rendering the app for it
  // would paint a screen whose every request answers 403, and the local flag
  // is advisory (stale the moment the address is confirmed elsewhere), so it
  // may HOLD the paint but must never route on its own — the redirect above
  // belongs to `pendingEmailVerification`, which the server owns.
  const holdsForVerificationAnswer =
    isAuthenticated &&
    !isInitialized &&
    AuthTokenService.isEmailUnverifiedSync();

  useEffect(() => {
    // The return path keeps its query string: `/connect?client_id=…&state=…`
    // (OAuth consent) is meaningless without it.
    const returnTo =
      pathname && pathname !== '/'
        ? `${pathname}${window.location.search}`
        : null;
    if (needsEmailVerification) {
      router.replace(withRedirectParam('/auth/verify-email', returnTo));
    } else if (needsLogin) {
      router.replace(withRedirectParam('/auth/login', returnTo));
    }
  }, [needsEmailVerification, needsLogin, pathname, router]);

  // Show a centered spinner until the stored session is readable, and during
  // login / logout — the store hydrates in a layout effect, so this state is
  // gone before the browser paints for a returning user. It no longer waits
  // for token validation: that answer arrives a round trip later, and holding
  // the whole app for it put a spinner on every cold open of every page.
  if (!isHydrated || loading || holdsForVerificationAnswer || (deviceSignInPending && !isAuthenticated)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // While the redirect effect runs we render nothing — keeps protected layout
  // out of the DOM for unauthenticated/unverified users.
  if (needsEmailVerification || needsLogin) {
    return null;
  }

  return (
    <>
      {/* ADR-001 §5: required after a purchase that cannot be restored
          without a sign-in method; ends when the server flag does. */}
      <RequiredSignInMethodOverlay />
      <ReceiptEmailDialog />
    <div
      className={cn(
        'bg-panel min-h-screen',
        hasFullHeightDesktopLayout && 'desktop-full-height-layout',
        // The AI architect docks as a column at the trailing edge of a wide
        // window (width published as the var below, 12px gutter). Full-height
        // pages pad their own scroll container instead, so its scrollbar stays
        // at the window edge; every other page narrows here.
        isAssistantOpen &&
          !hasFullHeightDesktopLayout &&
          'lg:pe-[var(--content-column-inline-end,324px)]'
      )}
    >
      <GlobalLoadingBar isLoading={isGlobalLoading} />

      {/* Single always-mounted owner of the persisted active building. Lives
          here (not in a nav) because the desktop sidebar only mounts on
          wide+expanded screens, so it could not reliably persist the choice. */}
      <ActiveProjectSync />

      <SubscriptionNotifications />

      {/* Main content with proper padding */}
      <main
        className={cn(
          'pb-20 lg:pb-0',
          hasFullHeightDesktopLayout && 'desktop-full-height-main'
        )}
      >
        {/* Page content */}
        {children}
      </main>

      {/* Bottom Navigation - floating island, visible below lg when sidebar is hidden */}
      {!layoutConfig.hideMobileNavigation && <MobileNavigation />}

      {/* Shared create-building dialog — opened from the project header ⋮ menu,
          the project switcher list and the no-buildings empty state. */}
      <NewProjectDialog />

      {/* Quick feedback raised from any page's ⋮ menu on phones. Mounted here,
          not inside the menus: a dialog owned by a menu would unmount with it
          on the very click that opens it. See `FeedbackMenuItem`. */}
      <FeedbackQuickDialog />

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp />

      {/* First-run wizard: sends a new account to /start once per session
          while the server says it has not started a design yet. */}
      <FirstRunGate />

      {/* Satisfaction survey — opens only when the server says the user is due
          (`should_request_feedback`) and no generation is in flight. */}
      <AppRatingAutoStart />

      {/* AI architect: "Ask AI" button, proactive moment, panel. */}
      <AssistantRoot aboveBottomNav={!layoutConfig.hideMobileNavigation} />
    </div>
    </>
  );
}
